import { useTranslation } from 'react-i18next';
import { Bell, Building, ChevronDown, Search } from 'lucide-react';
import { LocaleToggle } from '@/components/LocaleToggle';
import { useAgency } from '@/auth/AgencyProvider';

/**
 * The top bar: global search on the left, notifications with a numeric badge, tenant selector on the
 * right (docs/estilo-visual.md §Layout).
 */
export function Topbar({ notificationCount = 0 }: { notificationCount?: number }) {
  const { t } = useTranslation();
  const agency = useAgency();
  const agencyName = agency.status === 'ready' ? agency.agency.name : t('topbar.noWorkspace');

  return (
    <header className="sticky top-0 z-(--z-sticky) flex h-16 shrink-0 items-center gap-4 border-b border-hairline bg-card px-6">
      <div className="relative min-w-0 flex-1 md:max-w-xl">
        <Search
          className="pointer-events-none absolute top-1/2 left-3 size-5 -translate-y-1/2 text-ink-muted"
          aria-hidden="true"
        />
        {/* Search is not wired: there is nothing to search until the first table exists. Disabled and
            saying so beats a box that swallows what a dispatcher types. */}
        <input
          type="search"
          disabled
          aria-label={t('topbar.searchLabel')}
          placeholder={t('topbar.searchPlaceholder')}
          className="h-touch w-full rounded-md border border-hairline bg-sunken pr-3 pl-10 text-base text-foreground placeholder:text-ink-muted disabled:cursor-not-allowed"
        />
      </div>

      <div className="ml-auto flex items-center gap-2">
        <LocaleToggle />
        <button
          type="button"
          disabled
          aria-label={
            notificationCount > 0
              ? t('topbar.notificationCount', { count: notificationCount })
              : t('topbar.notifications')
          }
          className="relative grid size-touch place-items-center rounded-md text-ink-secondary hover:bg-secondary disabled:opacity-60"
        >
          <Bell className="size-5" aria-hidden="true" />
          {/* The badge renders only when there is something to count. A hardcoded "3" would be a
              screenshot, not a portal. */}
          {notificationCount > 0 && (
            <span className="tabular-figures absolute top-1 right-1 grid min-w-5 place-items-center rounded-full bg-destructive px-1 text-xs font-semibold text-destructive-foreground">
              {notificationCount}
            </span>
          )}
        </button>

        {/* Tenant selector: the current agency (AgencyProvider). Still disabled: switching needs the
            dashboard and invoice hooks to filter by agency too (design 2, multi-agency later). */}
        <button
          type="button"
          disabled
          className="flex h-touch items-center gap-2 rounded-md border border-hairline px-3 text-base text-ink-secondary disabled:opacity-70"
        >
          <Building className="size-5 text-ink-muted" aria-hidden="true" />
          <span className="max-w-48 truncate" title={agencyName}>
            {agencyName}
          </span>
          <ChevronDown className="size-4 text-ink-muted" aria-hidden="true" />
        </button>
      </div>
    </header>
  );
}
